import { useCallback, useEffect, useState } from "react";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";

import { ApiError } from "./api/client";
import type { Me, TeamMember } from "./api/types";
import { SignInScreen } from "./auth/SignInScreen";
import { useApiClient } from "./auth/useApiClient";
import { CalendarPage } from "./pages/CalendarPage";
import { DashboardPage } from "./pages/DashboardPage";
import { TeamPage } from "./pages/TeamPage";
import { PersonPicker } from "./components/PersonPicker";
import { ModeToggle } from "./components/ModeToggle";
import { JenlyLogo } from "./components/JenlyLogo";
import { applyMode, watchSystemMode, type ThemeMode } from "./theme/mode";
import { addMonths, currentMonth, type MonthRef } from "./lib/dates";

type View = "calendar" | "dashboard" | "team";

function Shell(): JSX.Element {
  const api = useApiClient();
  const { instance } = useMsal();

  const [me, setMe] = useState<Me | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View>("calendar");
  const [month, setMonth] = useState<MonthRef>(currentMonth);
  const [team, setTeam] = useState<TeamMember[]>([]);
  // Whose calendar is on screen. Null means "my own"; only admins can change it.
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((profile) => {
        if (cancelled) return;
        setMe(profile);
        applyMode(profile.theme);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadError(
          error instanceof ApiError ? error.message : "Could not reach the time tracker.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // While the preference is "system", the page has to re-resolve whenever the OS flips
  // between light and dark, not just on load.
  useEffect(() => {
    if (!me) return;
    return watchSystemMode(me.theme as ThemeMode, () => applyMode(me.theme));
  }, [me]);

  const refreshTeam = useCallback(async () => {
    if (!me?.is_privileged) return;
    try {
      setTeam(await api.team(`${month.year}-${String(month.month).padStart(2, "0")}`));
    } catch {
      // The roster is a convenience here; the Team tab surfaces its own errors.
    }
  }, [api, me?.is_privileged, month]);

  useEffect(() => {
    void refreshTeam();
  }, [refreshTeam]);

  if (loadError) {
    return (
      <div className="centred-screen">
        <div className="card sign-in-card">
          <h1>No access</h1>
          <p className="muted">{loadError}</p>
          <button
            type="button"
            className="ghost"
            onClick={() => void instance.logoutRedirect()}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="centred-screen">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const targetId = me.is_privileged ? viewingUserId : null;

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <JenlyLogo variant="mark" height={30} />
          <div>
            <h1>Team Time Tracker</h1>
            <p className="muted small">
              {me.display_name} · {me.role === "subadmin" ? "Sub-admin" : me.role}
            </p>
          </div>
        </div>

        <nav className="tabs" aria-label="Sections">
          <button
            type="button"
            className={view === "calendar" ? "tab active" : "tab"}
            onClick={() => setView("calendar")}
          >
            Calendar
          </button>
          <button
            type="button"
            className={view === "dashboard" ? "tab active" : "tab"}
            onClick={() => setView("dashboard")}
          >
            Dashboard
          </button>
          {me.is_privileged && (
            <button
              type="button"
              className={view === "team" ? "tab active" : "tab"}
              onClick={() => setView("team")}
            >
              Team
            </button>
          )}
        </nav>

        <div className="header-actions">
          <ModeToggle
            current={me.theme}
            onChange={(mode) => {
              // Apply immediately so the switch feels instant, then persist.
              applyMode(mode);
              setMe({ ...me, theme: mode });
              void api.setTheme(mode).then(setMe).catch(() => applyMode(me.theme));
            }}
          />
          <button
            type="button"
            className="ghost"
            onClick={() => void instance.logoutRedirect()}
          >
            Sign out
          </button>
        </div>
      </header>

      {me.is_privileged && view !== "team" && (
        <PersonPicker
          team={team}
          me={me}
          value={viewingUserId}
          onChange={setViewingUserId}
        />
      )}

      <main className="app-main">
        {view === "calendar" && (
          <CalendarPage
            api={api}
            me={me}
            month={month}
            onMonthChange={setMonth}
            onStepMonth={(delta) => setMonth((current) => addMonths(current, delta))}
            targetUserId={targetId}
          />
        )}
        {view === "dashboard" && (
          <DashboardPage
            api={api}
            month={month}
            onMonthChange={setMonth}
            onStepMonth={(delta) => setMonth((current) => addMonths(current, delta))}
            targetUserId={targetId}
          />
        )}
        {view === "team" && me.is_privileged && (
          <TeamPage api={api} me={me} month={month} onChanged={refreshTeam} />
        )}
      </main>
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <>
      <AuthenticatedTemplate>
        <Shell />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInScreen />
      </UnauthenticatedTemplate>
    </>
  );
}
